// src/components/FullScreenPreview.js
import React, { useEffect, useState } from 'react';
import PreviewUserProfile from './PreviewUserProfile';
import { ArrowLeft, Upload, ExternalLink } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import styles from './FullScreenPreview.module.css';

const FullScreenPreview = ({ isOpen, onClose, userId, onPublish }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Function to open the profile in a new tab
  const openProfileInNewTab = () => {
    if (userData && userData.username) {
      const profileUrl = `${window.location.origin}/${userData.username}`;
      window.open(profileUrl, '_blank');
    }
  };

  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Apply body style when modal is open to prevent scrolling underneath
  useEffect(() => {
    if (isOpen) {
      // Save the current overflow style to restore it when closing
      const originalStyle = document.body.style.overflow;
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Restore the original overflow style when component unmounts
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchDraftProfile = async () => {
      if (!isOpen || !userId) return;
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          setUserData({ ...userDoc.data(), uid: userId });
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error('Error fetching draft:', error);
        setUserData(null);
      }
      setLoading(false);
    };

    fetchDraftProfile();
  }, [isOpen, userId]);

  // If not open, return nothing
  if (!isOpen) return null;

  return (
    <div className={styles.fullScreenModal}>
      {/* Top bar with "Back", "Publish" and "Visit" */}
      <div className={styles.previewControls}>
        <div className={styles.buttonGroup}>
          <button onClick={onClose} className={styles.controlButton}>
            <ArrowLeft size={18} />
            <span>{isMobile ? "Back" : "Back to Editor"}</span>
          </button>

          <button onClick={onPublish} className={styles.controlButton}>
            <Upload size={18} />
            <span>{isMobile ? "Publish" : "Publish Changes"}</span>
          </button>

          <button onClick={openProfileInNewTab} className={styles.controlButton}>
            <ExternalLink size={18} />
            <span>{isMobile ? "Visit" : "Visit Published"}</span>
          </button>
        </div>
        <div className={styles.controlsInfo}>
          <span>Preview Mode</span>
        </div>
      </div>

      {/* Main preview area */}
      <div className={styles.previewContainer}>
        {loading ? (
          <div className={styles.loading}>Loading preview...</div>
        ) : userData ? (
          <PreviewUserProfile userData={userData} />
        ) : (
          <div className={styles.error}>Draft data unavailable for preview.</div>
        )}
      </div>
    </div>
  );
};

export default FullScreenPreview;